import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { formableIntents, intentTitle, payloadSchema } from '../model/manifest'
import { orderedProperties, resolve, type Schema } from '../model/schema'
import { DragHandle } from '../ui/DragHandle'
import { OptionList } from '../ui/OptionList'
import { RowMenu } from '../ui/RowMenu'
import { useReorder } from '../ui/reorder'
import { IntentForm } from './IntentForm'
import { SchemaForm } from './SchemaForm'
import type { EditorProps } from './types'
import { shown } from '../api/useIntent'

/**
 * A Form's fields, built one at a time.
 *
 * **This is the last bespoke editor phase 4 adds, and the reason is worth
 * being precise about.** A Form's inputs are *named*, not linked: `name`
 * is the field's identity, `form.field_removed` names one, and
 * `form.fields_reordered` carries a bare `[]string`. So none of them trip
 * the placement markers, the composition control correctly does not draw
 * them, and `form.fields_reordered` has been sitting under "no control
 * yet" ever since the split was computed. It is a list of records keyed
 * by a member the schema cannot point at, which is exactly the thing a
 * derived control cannot know and a written one can.
 *
 * Everything a schema *can* say is still taken from it. The field dialog
 * is `SchemaForm` over the `field` member of the intent's own payload, so
 * a field's members are never listed here — and `type` renders as a
 * select because `form.FieldType` became a named vocabulary for this, by
 * the rule that a closed set earns one when an intent can change it. The
 * alternative was this file holding the eight input kinds, which is the
 * bug that had already lost `xs` from a hand-written spacing list.
 *
 * Anything the type declares that this editor has no section for is drawn
 * from its payload anyway, so a sixth Form intent is reachable the day it
 * is registered.
 */

/** The intents the builder itself sends. Everything else falls through. */
const BUILT = new Set([
  'form.field_added',
  'form.field_updated',
  'form.field_removed',
  'form.fields_reordered',
  'form.settings_set',
])

interface FormField {
  name?: string
  type?: string
  label?: string
  placeholder?: string
  required?: boolean
  options?: string[]
  role?: string
}

export function FormEditor({ fields, manifest, typeName, busy, register, send }: EditorProps) {
  const [editing, setEditing] = useState<{ field: FormField; existing?: string } | null>(null)

  const declared = (Array.isArray(fields.fields) ? fields.fields : []) as FormField[]
  const names = declared.flatMap((field) => (field.name ? [field.name] : []))

  const addSchema = payloadSchema(manifest, typeName, 'form.field_added')
  const updateSchema = payloadSchema(manifest, typeName, 'form.field_updated')
  const fieldSchema = memberSchema(addSchema, 'field') ?? memberSchema(updateSchema, 'field')
  const settingsSchema = payloadSchema(manifest, typeName, 'form.settings_set')

  const rest = formableIntents(manifest, typeName, fields).filter((intent) => !BUILT.has(intent.name))

  // The words this type declares for each control it names by hand. A
  // Form calls its inputs questions; nothing derived from
  // `form.field_added` could know that.
  const says = (intent: string) => intentTitle(manifest, typeName, intent)

  // Dragging a question to a new place, over the intent that already
  // carries the whole order. A form's inputs are *named* rather than
  // linked, so nothing about this list is derivable — which is why this
  // editor exists — but the gesture is the one every other ordered list
  // in this client uses, and it sends the same shape of command.
  const reorder = useReorder((_group, order) => {
    send('form.fields_reordered', { order }).catch(shown)
  }, !busy)

  const move = (name: string, direction: -1 | 1) => {
    const order = [...names]
    const from = order.indexOf(name)
    const to = from + direction
    if (from < 0 || to < 0 || to >= order.length) return
    ;[order[from], order[to]] = [order[to], order[from]]
    // The whole order, for the reason every reorder in this client sends
    // the whole order: one that omits an entry keeps it, so a short array
    // can never remove and the intent says exactly what the author sees.
    send('form.fields_reordered', { order }).catch(shown)
  }

  const save = (field: FormField, existing?: string) => {
    const promise =
      existing === undefined
        ? // The position is an index into the list, which this control
          // knows and an author would only be guessing at — the same
          // reason `x-position` exists.
          send('form.field_added', { position: declared.length, field })
        : send('form.field_updated', { name: existing, field })
    void promise.then(() => setEditing(null))
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h3">Fields</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={busy || !fieldSchema}
            onClick={() => setEditing({ field: {} })}
          >
            {says('form.field_added')}
          </Button>
        </Stack>

        {declared.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This form asks nothing yet.
          </Typography>
        ) : (
          <Paper variant="outlined">
            {declared.map((field, index) => (
              <Box key={field.name ?? index}>
                {index > 0 && <Divider />}
                <Stack
                  {...(field.name && names.length > 1 ? reorder.rowProps('', field.name, names) : {})}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    opacity: field.name && reorder.isHeld('', field.name) ? 0.4 : 1,
                    borderTop: field.name && reorder.landingOn('', field.name) === 'above' ? 2 : 0,
                    borderBottom: field.name && reorder.landingOn('', field.name) === 'below' ? 2 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  {names.length > 1 && (
                    <DragHandle label={says('form.fields_reordered')} disabled={busy || !field.name} />
                  )}
                  <Chip size="small" label={field.name ?? '(unnamed)'} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {field.label || field.name}
                  </Typography>
                  <Chip size="small" variant="outlined" label={field.type} />
                  {field.required && <Chip size="small" variant="outlined" color="primary" label="required" />}
                  {/* A role is what the answer *is*, and it is what the
                      delivered email reads to know which address to reply
                      to and what to put in the subject line. Shown on the
                      row because it is invisible to a visitor and
                      load-bearing to the site owner. */}
                  {field.role && <Chip size="small" variant="outlined" color="secondary" label={field.role} />}
                  {(field.options?.length ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {field.options?.length} options
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <RowMenu
                    disabled={busy}
                    actions={[
                      {
                        label: 'Move up',
                        icon: <ArrowUpwardIcon fontSize="small" />,
                        disabled: index === 0 || !field.name,
                        detail: says('form.fields_reordered'),
                        onSelect: () => field.name && move(field.name, -1),
                      },
                      {
                        label: 'Move down',
                        icon: <ArrowDownwardIcon fontSize="small" />,
                        disabled: index === declared.length - 1 || !field.name,
                        detail: says('form.fields_reordered'),
                        onSelect: () => field.name && move(field.name, 1),
                      },
                      {
                        label: says('form.field_updated'),
                        icon: <EditOutlinedIcon fontSize="small" />,
                        disabled: !fieldSchema,
                        onSelect: () => setEditing({ field, existing: field.name }),
                      },
                      {
                        label: says('form.field_removed'),
                        icon: <DeleteOutlineIcon fontSize="small" />,
                        destructive: true,
                        disabled: !field.name,
                        onSelect: () => field.name && send('form.field_removed', { name: field.name }).catch(shown),
                      },
                    ]}
                  />
                </Stack>
              </Box>
            ))}
          </Paper>
        )}

      </Stack>

      {settingsSchema && (
        <DeliverySection
          schema={settingsSchema}
          fields={fields}
          label={says('form.settings_set')}
          showName={register === 'build'}
          busy={busy}
          send={send}
        />
      )}

      {rest.map((intent) => (
        <IntentForm
          key={intent.name}
          intent={intent}
          fields={fields}
          showName={register === 'build'}
          busy={busy}
          send={send}
        />
      ))}

      {editing && fieldSchema && (
        <FieldDialog
          schema={fieldSchema}
          field={editing.field}
          existing={editing.existing}
          taken={names}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </Stack>
  )
}

/**
 * Where a form's answers go, and what a visitor meets before sending
 * them.
 *
 * **Written rather than derived, for one member.** Everything here except
 * `recipients` is drawn from the intent's own schema, so the challenge
 * vocabulary and the action kinds are the platform's list and not a copy
 * of it. `recipients` gets `OptionList` by hand — the same control
 * `SchemaForm` would now give it — for the sentence that goes with it:
 * these addresses are stored and never served, and an author handing over
 * a site owner's email deserves to be told that where they type it.
 *
 * The warnings under it are the reason this section exists at all. The
 * two members are a pair the platform refuses to save half of, and the
 * pairing is a rule in the fold that no schema publishes — so a derived
 * form can offer both and cannot say that naming an action without a
 * recipient is a form nobody will ever hear from.
 *
 * The whole payload goes every time, because the fold assigns the struct
 * it is handed: a member left out is cleared rather than kept.
 */
function DeliverySection({
  schema,
  fields,
  label,
  showName,
  busy,
  send,
}: {
  schema: Schema
  fields: Record<string, unknown>
  label: string
  /** The intent's own name under the section, which only build mode says. */
  showName: boolean
  busy: boolean
  send: EditorProps['send']
}) {
  const starting = useMemo(
    () => ({
      challenge: typeof fields.challenge === 'string' ? fields.challenge : '',
      action: (fields.action as Record<string, unknown> | undefined) ?? {},
      recipients: Array.isArray(fields.recipients) ? (fields.recipients as string[]) : [],
    }),
    [fields],
  )
  const [value, setValue] = useState<Record<string, unknown>>(starting)
  const startingKey = JSON.stringify(starting)
  useEffect(() => setValue(starting), [startingKey])

  const scalars: Schema = {
    ...schema,
    properties: Object.fromEntries(
      orderedProperties(schema, schema)
        .filter((property) => property.name !== 'recipients')
        .map((property) => [property.name, property.schema]),
    ),
  }

  const recipients = Array.isArray(value.recipients) ? (value.recipients as string[]) : []
  const action = (value.action as Record<string, unknown> | undefined) ?? {}
  const delivers = action.kind === 'email'
  const dirty = JSON.stringify(value) !== startingKey

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">Where answers go</Typography>

      <SchemaForm schema={scalars} root={schema} value={value} onChange={setValue} disabled={busy} />

      <Stack spacing={1}>
        <Typography variant="subtitle2">Recipients</Typography>
        <Typography variant="caption" color="text.secondary">
          Every submission is emailed to these addresses. Visitors never see them.
        </Typography>
        <OptionList
          options={recipients}
          label="Add a recipient"
          disabled={busy}
          onChange={(next) => setValue({ ...value, recipients: next })}
        />
      </Stack>

      {delivers && recipients.length === 0 && (
        <Alert severity="warning">
          Add at least one recipient. Without one, this cannot be saved, because a submission would reach nobody.
        </Alert>
      )}
      {!delivers && recipients.length > 0 && (
        <Alert severity="warning">
          This form is not set to email its submissions, so these addresses are not used.
        </Alert>
      )}
      {String(value.challenge ?? '') === 'turnstile' && (
        <Alert severity="info">
          The Turnstile spam check is shown on the site's form. Submissions are still accepted here if the check is
          missing.
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={busy || !dirty} onClick={() => send('form.settings_set', value).catch(shown)}>
          {label}
        </Button>
        <Button disabled={busy || !dirty} onClick={() => setValue(starting)}>
          Discard
        </Button>
      </Stack>

      {showName && (
        <Typography variant="caption" color="text.secondary">
          form.settings_set
        </Typography>
      )}
    </Stack>
  )
}

/**
 * One field, edited.
 *
 * **Every member comes from the schema, `options` included.** It used to
 * be drawn by hand, for a note that had to sit beside it: which input
 * kinds accept options was a rule the platform held and the schema did
 * not publish, so the list was offered against a text field and a phone
 * number and the surface refused the pairing afterwards. The schema
 * publishes it now — the choices the declaration itself names — so the
 * list appears when the kind takes one and the note has nothing left to
 * explain.
 */
function FieldDialog({
  schema,
  field,
  existing,
  taken,
  busy,
  onClose,
  onSave,
}: {
  schema: Schema
  field: FormField
  /** The name this field is stored under, absent when it is new. */
  existing?: string
  taken: string[]
  busy: boolean
  onClose: () => void
  onSave: (field: FormField, existing?: string) => void
}) {
  const [value, setValue] = useState<Record<string, unknown>>({})
  useEffect(() => setValue({ ...field }), [field])

  const name = typeof value.name === 'string' ? value.name.trim() : ''
  const type = typeof value.type === 'string' ? value.type : ''
  const clashes = name !== existing && taken.includes(name)

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{existing === undefined ? 'Add a field' : `Edit ${existing}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <SchemaForm schema={schema} root={schema} value={value} onChange={setValue} disabled={busy} />

          {clashes && (
            <Alert severity="error">
              This form already has a field named <code>{name}</code>. Each field needs a different name, because the
              name labels its answer in the emails you receive.
            </Alert>
          )}

          {existing !== undefined && name !== existing && (
            <Alert severity="warning">
              Renaming a field changes the key its answers arrive under. Anything already reading{' '}
              <code>{existing}</code> stops seeing it.
            </Alert>
          )}

        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || !name || !type || clashes}
          onClick={() => onSave(value as FormField, existing)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** One named member of an object schema, resolved. */
function memberSchema(schema: Schema | undefined, member: string): Schema | undefined {
  if (!schema) return undefined
  return resolve(schema.properties?.[member], schema)
}
